import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
    <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{title}</h2>
        <div className="text-slate-600 leading-7 space-y-3">{children}</div>
    </div>
);

const TermsOfService = () => {
    useEffect(() => { window.scrollTo(0, 0); }, []);

    return (
        <div className="h-screen overflow-y-auto bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                        <span className="text-[17px] font-bold tracking-tight text-slate-900">Career Champ</span>
                    </Link>
                    <Link to="/login" className="text-sm font-medium text-[#3F57E0] hover:underline">
                        Back to login
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-12">
                {/* Title */}
                <div className="mb-10">
                    <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-2">Terms of Service</h1>
                    <p className="text-sm text-slate-400">Last updated: May 2026</p>
                </div>

                <Section title="1. Acceptance of Terms">
                    <p>
                        By accessing or using Career Champ ("the Service"), you agree to be bound by these Terms of Service.
                    </p>
                    <p>
                        These terms apply to all users, including visitors, registered users, and paying subscribers.
                    </p>
                </Section>

                <Section title="2. Description of Service">
                    <p>
                        Career Champ is a job search platform that helps users discover companies, find recruiters, track hiring signals, generate AI-powered outreach messages, and manage their job search pipeline. The Service integrates with LinkedIn (via Unipile), Google, and other third-party providers.
                    </p>
                </Section>

                <Section title="3. Account Registration">
                    <p>You may register using an email address and password or via Google OAuth. You are responsible for:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Providing accurate and complete registration information</li>
                        <li>Maintaining the security of your account credentials</li>
                        <li>All activity that occurs under your account</li>
                    </ul>
                    <p>
                        We reserve the right to suspend or terminate accounts that violate these terms.
                    </p>
                </Section>

                <Section title="4. Credits and Subscriptions">
                    <p>
                        Career Champ operates on a credit-based system. Credits are consumed when you search for companies, reveal recruiter contacts, and use AI features. Free accounts receive a starting credit balance.
                    </p>
                    <p>
                        Paid subscriptions and credit top-ups are processed through Stripe. All payments are non-refundable unless otherwise required by applicable law. Subscription plans renew automatically unless cancelled before the renewal date.
                    </p>
                </Section>

                <Section title="5. LinkedIn Integration">
                    <p>
                        Career Champ connects to LinkedIn through Unipile, a third-party integration provider. By connecting your LinkedIn account, you authorise Career Champ to send connection requests, direct messages, and perform searches on your behalf as directed by you.
                    </p>
                    <p>
                        You are solely responsible for ensuring your use of LinkedIn features through our platform complies with LinkedIn's own Terms of Service. Career Champ is not affiliated with LinkedIn.
                    </p>
                </Section>

                <Section title="6. AI-Generated Content">
                    <p>
                        Career Champ uses AI (DeepSeek, Perplexity) to generate outreach messages, strategy advice, and hiring signal analysis. AI-generated content is provided as a starting point and may not always be accurate or appropriate. You are responsible for reviewing and editing any content before sending it.
                    </p>
                </Section>

                <Section title="7. Acceptable Use">
                    <p>You agree not to:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Use the Service for spam, harassment, or unsolicited mass outreach</li>
                        <li>Attempt to reverse-engineer, scrape, or misuse the platform</li>
                        <li>Violate any applicable law or regulation</li>
                        <li>Share your account credentials with others</li>
                        <li>Use the Service in any way that violates LinkedIn's or any third-party's terms</li>
                    </ul>
                </Section>

                <Section title="8. Intellectual Property">
                    <p>
                        All content, design, and software comprising the Service are owned by Career Champ or its licensors. You may not copy, reproduce, or distribute any part of the Service without written permission.
                    </p>
                    <p>
                        You retain ownership of any content you submit (e.g., resume, profile data). By submitting content, you grant Career Champ a limited licence to process it in order to provide the Service.
                    </p>
                </Section>

                <Section title="9. Disclaimers">
                    <p>
                        The Service is provided "as is" without warranties of any kind. Career Champ does not guarantee job placement, recruiter responses, or hiring outcomes. Third-party data (recruiter emails, company information) is sourced from external providers and may not always be accurate or up to date.
                    </p>
                </Section>

                <Section title="10. Limitation of Liability">
                    <p>
                        To the maximum extent permitted by law, Career Champ shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service, including but not limited to loss of data, lost profits, or reputational harm.
                    </p>
                </Section>

                <Section title="11. Termination">
                    <p>
                        We reserve the right to suspend or terminate your access at any time for violations of these terms. You may also delete your account at any time by contacting us. Upon termination, your data will be handled in accordance with our Privacy Policy.
                    </p>
                </Section>

                <Section title="12. Changes to Terms">
                    <p>
                        We may update these Terms of Service from time to time. We will notify users of material changes via email or an in-app notice. Continued use of the Service after changes constitutes acceptance of the updated terms.
                    </p>
                </Section>

                <Section title="13. Governing Law">
                    <p>
                        These terms are governed by the laws of the United States. Any disputes shall be resolved in the courts of competent jurisdiction.
                    </p>
                </Section>

                <Section title="14. Contact">
                    <p>
                        For questions about these Terms of Service, contact us at{' '}
                        <a href="mailto:info@careerchamp.io" className="text-[#3F57E0] hover:underline">
                            info@careerchamp.io
                        </a>
                    </p>
                </Section>

                {/* Footer links */}
                <div className="mt-12 pt-6 border-t border-slate-200 flex gap-6 text-sm text-slate-500">
                    <Link to="/privacy" className="hover:text-[#3F57E0] hover:underline">Privacy Policy</Link>
                    <Link to="/login" className="hover:text-[#3F57E0] hover:underline">Back to login</Link>
                </div>
            </main>
        </div>
    );
};

export default TermsOfService;
